import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, Users, Eye, MousePointerClick, MessageCircle, Phone, ExternalLink,
  RefreshCw, Loader2, BarChart3,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const StatCard = ({ icon: Icon, label, value, hint, accent = 'orange' }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5">
    <div className="flex items-center justify-between">
      <div className={`w-10 h-10 rounded-xl bg-${accent}-100 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${accent}-600`} />
      </div>
    </div>
    <div className="mt-3 text-3xl font-bold text-slate-900">{value}</div>
    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
    {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
  </div>
);

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get('/admin/site-analytics');
      setData(data);
    } catch {
      // soft-fail; keep last data
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!user || user.email !== 'chiragahir@gmail.com') {
      // server enforces this anyway, but redirect non-admins early
      navigate('/dashboard', { replace: true });
      return;
    }
    load();
    // Auto-refresh every 30s while the dashboard is open — useful during
    // the launch campaign when the founder wants near-realtime visibility.
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="p-12 text-center text-slate-500">
          <Loader2 className="w-6 h-6 mx-auto animate-spin" />
        </div>
      </div>
    );
  }
  if (!data) return null;

  const funnel = data.funnelLast7d || {};
  const stages = [
    { key: 'page_view',          label: 'Page views' },
    { key: 'signup_start',       label: 'Signup starts' },
    { key: 'checkout_start',     label: 'Checkout starts' },
    { key: 'trial_started',      label: 'Trials started' },
    { key: 'whatsapp_subscribe', label: 'WhatsApp signups' },
  ];
  const maxFunnel = Math.max(1, ...stages.map(s => funnel[s.key] || 0));

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight flex items-center gap-3">
              <BarChart3 className="w-7 h-7 text-orange-500" /> Founder Analytics
            </h1>
            <p className="mt-1 text-slate-600 text-sm">
              Auto-refreshes every 30s · last update {new Date(data.now).toLocaleTimeString()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => navigate('/admin/journeys')} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="open-journeys">
              <Users className="w-4 h-4 mr-2" /> Customer journeys
            </Button>
            <Button variant="outline" onClick={load} disabled={refreshing} data-testid="analytics-refresh">
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Top stats */}
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Eye} label="Visits today" value={data.visitsToday} accent="orange" />
          <StatCard icon={Users} label="Unique sessions today" value={data.sessionsToday} accent="emerald" />
          <StatCard icon={MousePointerClick} label="CTA clicks today" value={data.ctaClicksToday} accent="rose" />
          <StatCard icon={MessageCircle} label="WhatsApp signups today" value={data.whatsappSignupsToday} accent="blue" />
        </div>

        {/* Funnel */}
        <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="text-lg font-bold text-slate-900">Funnel · last 7 days</h2>
          <div className="mt-4 space-y-3">
            {stages.map(s => {
              const count = funnel[s.key] || 0;
              const pct = Math.round((count / maxFunnel) * 100);
              return (
                <div key={s.key}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-slate-700">{s.label}</span>
                    <span className="font-bold text-slate-900">{count}</span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-orange-400 to-rose-500" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top pages + top CTAs */}
        <div className="mt-8 grid lg:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-orange-500" /> Top pages · last 7d
            </h3>
            <div className="mt-4 divide-y divide-slate-100">
              {(data.topPages || []).map(p => (
                <div key={p.path} className="flex items-center justify-between py-2 text-sm">
                  <span className="font-mono text-slate-700 truncate mr-3">{p.path || '/'}</span>
                  <span className="font-bold text-slate-900">{p.views}</span>
                </div>
              ))}
              {(data.topPages || []).length === 0 && (
                <p className="text-sm text-slate-400 py-2">No data yet — campaign hasn't driven traffic yet.</p>
              )}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <MousePointerClick className="w-4 h-4 text-orange-500" /> Top CTAs · last 7d
            </h3>
            <div className="mt-4 divide-y divide-slate-100">
              {(data.topCtas || []).map(c => (
                <div key={c.label} className="flex items-center justify-between py-2 text-sm">
                  <span className="text-slate-700">{c.label}</span>
                  <span className="font-bold text-slate-900">{c.clicks}</span>
                </div>
              ))}
              {(data.topCtas || []).length === 0 && (
                <p className="text-sm text-slate-400 py-2">No CTA clicks recorded yet.</p>
              )}
            </div>
          </div>
        </div>

        {/* UTM channel breakdown — which acquisition channel actually converts */}
        <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6" data-testid="utm-channel-breakdown">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-orange-500" /> Acquisition channels · last 7d
          </h3>
          <p className="mt-1 text-xs text-slate-500">
            First-touch attribution. Each session is bucketed under the first `utm_source` it ever showed up under.
            <b className="text-slate-700"> Sessions</b> = unique visitors. <b className="text-slate-700">Signups</b> = signup_complete events.
            <b className="text-slate-700"> Conv %</b> = signups ÷ sessions. Use this to kill ad sets that bring traffic but no signups.
          </p>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Source</th>
                  <th className="py-2 pr-3 text-right">Sessions</th>
                  <th className="py-2 pr-3 text-right">Signups</th>
                  <th className="py-2 pr-3 text-right">Conv %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data.utmChannelsLast7d || []).map((c) => {
                  const isWinner = c.conversionRate >= 2;
                  const isLoser = c.sessions >= 10 && c.signups === 0;
                  return (
                    <tr key={c.source} data-testid={`utm-row-${c.source}`}>
                      <td className="py-2.5 pr-3 font-mono text-slate-700 capitalize">{c.source}</td>
                      <td className="py-2.5 pr-3 text-right font-semibold text-slate-900">{c.sessions}</td>
                      <td className="py-2.5 pr-3 text-right font-semibold text-slate-900">{c.signups}</td>
                      <td className={`py-2.5 pr-3 text-right font-bold ${
                        isWinner ? 'text-emerald-600' : isLoser ? 'text-rose-600' : 'text-slate-700'
                      }`}>
                        {c.conversionRate}%
                        {isWinner && <span className="ml-1.5 text-xs">↑ winner</span>}
                        {isLoser && <span className="ml-1.5 text-xs">↓ kill it</span>}
                      </td>
                    </tr>
                  );
                })}
                {(data.utmChannelsLast7d || []).length === 0 && (
                  <tr><td colSpan={4} className="py-3 text-slate-400 text-sm text-center">No tracked sessions yet. Add `?utm_source=ig` etc. to your ad URLs to see this populate.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* A/B experiments */}
        {data.experimentViewsLast7d && Object.keys(data.experimentViewsLast7d).length > 0 && (
          <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-orange-500" /> Live A/B experiments · last 7d
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              Compare against signup_complete counts in the funnel above to pick a winner.
            </p>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              {Object.entries(data.experimentViewsLast7d).map(([label, views]) => (
                <div key={label} className="bg-slate-50 rounded-xl p-4">
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
                  <div className="mt-1 text-2xl font-bold text-slate-900">{views} views</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* WhatsApp leads */}
        <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-emerald-600" /> WhatsApp signups
              <span className="ml-2 text-xs font-medium text-slate-500">{(data.whatsappSubscribers || []).length} total shown</span>
            </h3>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Phone</th>
                  <th className="py-2 pr-3">Business</th>
                  <th className="py-2 pr-3 text-right">Reach out</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data.whatsappSubscribers || []).map(s => {
                  const phoneClean = (s.phone || '').replace(/\D/g, '');
                  const wa = phoneClean.startsWith('44')
                    ? phoneClean
                    : phoneClean.startsWith('0') ? `44${phoneClean.slice(1)}` : phoneClean;
                  const url = `https://wa.me/${wa}?text=${encodeURIComponent(`Hi ${s.name || ''}, this is the founder of MyBusinessAdPro 👋`)}`;
                  return (
                    <tr key={s.id} className="text-slate-700">
                      <td className="py-2 pr-3 text-xs text-slate-500">{(s.subscribedAt || '').slice(0, 16).replace('T', ' ')}</td>
                      <td className="py-2 pr-3 font-semibold">{s.name || '—'}</td>
                      <td className="py-2 pr-3"><Phone className="inline w-3 h-3 mr-1" />{s.phone || '—'}</td>
                      <td className="py-2 pr-3">{s.businessName || '—'}<span className="text-slate-400 text-xs ml-1">{s.businessType ? `(${s.businessType})` : ''}</span></td>
                      <td className="py-2 pr-3 text-right">
                        {wa ? (
                          <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-emerald-700 font-semibold hover:underline">
                            WhatsApp <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
                {(data.whatsappSubscribers || []).length === 0 && (
                  <tr><td colSpan={5} className="py-4 text-center text-slate-400">No signups yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent events feed */}
        <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6">
          <h3 className="font-bold text-slate-900">Recent events (non-page-view)</h3>
          <div className="mt-3 max-h-80 overflow-y-auto divide-y divide-slate-100 text-xs font-mono">
            {(data.recentEvents || []).map(e => (
              <div key={e.id} className="py-1.5 flex gap-3">
                <span className="text-slate-400 w-32 shrink-0">{(e.createdAt || '').slice(11, 19)}</span>
                <span className="text-orange-600 font-semibold w-32 shrink-0">{e.type}</span>
                <span className="text-slate-700 truncate">{e.label || e.path || '-'}</span>
              </div>
            ))}
            {(data.recentEvents || []).length === 0 && (
              <p className="py-2 text-slate-400">No events yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
